import { getUA } from "react-device-detect";
import {
  BRAND_LIST,
  BRAND_NAME_LIST,
  COUNTRY,
  DepartmentIdEnum,
  LANGUAGE,
  Maybe,
  StyleGenderEnum,
  StyleGenderType,
  UserTypeEnum,
  UserTypeNumberMap,
  UserTypeTypes
} from "@type/index";
import {
  GU_HK_Android_UserAgent,
  GU_HK_Ios_UserAgent,
  GU_TW_Android_UserAgent,
  GU_TW_Ios_UserAgent,
  SB_ENV,
  UQ_HK_Android_UserAgent,
  UQ_HK_Ios_UserAgent,
  UQ_TW_Android_UserAgent,
  UQ_TW_Ios_UserAgent
} from "@config/common";
import store from "../redux/store";
import { CurrencyFormatter } from "./currency";

export const isNotEmpty = (s: unknown): boolean => {
  if (s !== undefined && s !== null) {
    return true;
  } else {
    return false;
  }
};

export const isNotEmptyStr = (s: unknown): boolean => {
  if (typeof s === "string" && s.length > 0) {
    return true;
  }
  return false;
};

export const isNotEmptyObj = (s: any): boolean => {
  if (s === undefined || s === null) {
    return false;
  }
  const count = Object.keys(s).length;
  if (count > 0) {
    return true;
  }
  return false;
};

// Thousand separator
export const toThousands = (num: number | null): string => {
  if (num === null) {
    return "";
  }
  const result = [];
  let counter = 0;
  const _num = num.toString();
  const numArr = _num.split("."); // dividing decimal point
  const numStrs = numArr[0].toString().split(""); // convert to a character array
  for (let i = numStrs.length - 1; i >= 0; i -= 1) {
    counter += 1;
    result.unshift(numStrs[i]);
    if (!(counter % 3) && i !== 0) {
      result.unshift(",");
    }
  }
  if (numArr[1]) {
    return result.join("").concat(`.${numArr[1]}`);
  } else {
    return result.join("");
  }
};

function formatDecimal(num: number, decimal: number) {
  let newNum = num.toString();
  const index = newNum.indexOf(".");
  if (index !== -1) {
    newNum = newNum.substring(0, decimal + index + 1);
    newNum = parseFloat(newNum).toFixed(decimal);
  } else {
    newNum = toThousands(Number(newNum));
  }
  return newNum;
}

export const formatTotalNum = (num: number) => {
  const si = [
    { range: 1, symbol: "", value: 1 },
    { range: 1e4, symbol: "K", value: 1e3 },
    { range: 1e6, symbol: "M", value: 1e6 },
    { range: 1e9, symbol: "B", value: 1e9 }
  ];
  let i;
  for (i = si.length - 1; i > 0; i -= 1) {
    if (num >= si[i].range) {
      break;
    }
  }
  return formatDecimal(num / si[i].value, 1) + si[i].symbol;
};

// Prevents events from bubbling
export const preventBubbling = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => {
  e.stopPropagation();
  e.preventDefault();
};

// Get the position where the string appears
export const findStrIndex = (str: string, cha: string, num: number) => {
  let x = 0;
  for (let i = 0; i < num; i += 1) {
    x = str.indexOf(cha, x + 1);
  }
  return x;
};

// Store link by brand region language
export const getStoreLinkUrl = (
  brand: string,
  domain: string,
  region: string,
  language: string,
  storeId: number
): string => {
  switch (brand) {
    case BRAND_LIST.UQ:
      return `${domain}/${region}/${language}/detail/${storeId}`;
    case BRAND_LIST.GU:
      return `${domain}/${region}/stores/`;
    default:
      return "";
  }
};

// Handle store link
export const handleStoreLink = (
  id: number,
  storeRegion: string
): string | undefined => {
  const { brand_name, region, language } = store.getState().config;
  if (
    storeRegion &&
    storeRegion.toLocaleUpperCase() !== region.toLocaleUpperCase()
  ) {
    return undefined;
  } else {
    const domain = SB_ENV(brand_name, region).storeDomain;
    return getStoreLinkUrl(brand_name, domain, region, language, id);
  }
};

// Capitalize the first letter
export const titleCase = (str: string): string => {
  let newStr = "";
  if (str && str.length > 0) {
    newStr = str.slice(0, 1).toUpperCase() + str.slice(1).toLowerCase();
  }
  return newStr;
};

// Get brand name
export const getBrandName = (): string => {
  const { brand_name } = store.getState().config;
  switch (brand_name) {
    case BRAND_LIST.UQ:
      return titleCase(BRAND_NAME_LIST.UQ);
    case BRAND_LIST.GU:
      return BRAND_NAME_LIST.GU.toUpperCase();
    default:
      return titleCase(BRAND_NAME_LIST.UQ);
  }
};

// Gender mapping
const genderMapper: { [key: string]: string } = {
  MEN: "text_men",
  WOMEN: "text_women",
  KIDS: "text_kids",
  BABY: "text_baby",
  UNISEX: "text_unisex"
};

// Convert gender enum to LID
export const localizeGenderName = (genderCategory: string | undefined) => {
  if (!genderCategory) return "";
  return genderMapper[genderCategory] || "";
};

/**
 * Function checks the size of device and returns if its a mobile
 * or not, for IPad it will be false
 * Function uses the same width as DS breakpoints
 */
export const isSP = () => window && window.innerWidth < 600;

// Get gender code from gender name
// 1 = MEN, 2 = WOMEN, 3 = KIDS,  4 = BABY
export const handleGetGenderCode = (
  gender: DepartmentIdEnum
): StyleGenderType => {
  switch (gender.toUpperCase()) {
    case StyleGenderEnum.WOMEN:
      return 2;
    case StyleGenderEnum.MEN:
      return 1;
    case StyleGenderEnum.KIDS:
      return 3;
    case StyleGenderEnum.BABY:
      return 4;
    default:
      return 2;
  }
};
export const handleGetGenderValue = (gender: number): string => {
  switch (gender) {
    case 1:
      return StyleGenderEnum.MEN;
    case 2:
      return StyleGenderEnum.WOMEN;
    case 3:
      return StyleGenderEnum.KIDS;
    case 4:
      return StyleGenderEnum.BABY;
    default:
      return StyleGenderEnum.WOMEN;
  }
};

/**
 * Primary user_type values: 0 = consumer, 1 = official, 2 = staff, 3 = KOL, 4 = KOL(badge), 5 = stylehinter
 * Only available for stylehint users. Field not available if instagram user.
 */
export const handleGetUserTypeValue = (type: number): UserTypeEnum =>
  UserTypeNumberMap[type] || UserTypeEnum.CONSUMER;

// Get About App URL
export const getAboutAppURL = (region: string, language: string): string => {
  const _language =
    language === LANGUAGE.ENGB
      ? "en"
      : handleSymbolReplace(language.toLowerCase());
  return `${process.env.REACT_APP_STYLEHINT_DOMAIN}/${region}/${_language}/about_app`;
};

/**
 * @param priceValue - a price value
 */
export const formatPrice = (priceValue: number): string => {
  const { region, language } = store.getState().config;
  const price = new CurrencyFormatter(region as COUNTRY, language as LANGUAGE);
  return price.format(priceValue);
};

// Get product code from product link from ugc
export const getProductCode = (str: string): string => {
  const index = str.indexOf("productCode");
  if (str.length > 0 && index !== -1) {
    const newStr = str.slice(index);
    return newStr.split("=")[1];
  } else {
    return "";
  }
};

// For GU, GA value should be KIDS·BABY
export const gaGenderValue = (gender: string, isUpper?: boolean): string => {
  const { brand_name } = store.getState().config;
  if (brand_name === BRAND_LIST.GU && gender.toLowerCase() === "kids") {
    return isUpper ? "KIDS・BABY" : "kids・baby";
  }
  return isUpper ? gender.toUpperCase() : gender.toLowerCase();
};

// GUHK App login
export const isInGuHkIosApp = () => getUA.indexOf(GU_HK_Ios_UserAgent) > -1;
export const isInGuHkAndroidApp = () =>
  getUA.indexOf(GU_HK_Android_UserAgent) > -1;
export const iosAndroidGuHk = (method: string) => {
  if (isInGuHkIosApp()) {
    window.webkit.messageHandlers[method].postMessage("");
    return false;
  } else if (isInGuHkAndroidApp()) {
    window.android[method]("", "", "");
    return false;
  } else {
    return true;
  }
};

// UQHK App login and styling wish(add and delete)
export const isInUqHkIosApp = () => getUA.indexOf(UQ_HK_Ios_UserAgent) > -1;
export const isInUqHkAndroidApp = () =>
  getUA.indexOf(UQ_HK_Android_UserAgent) > -1;
export const iosAndroidUqHk = (method: string, code: string) => {
  if (isInUqHkIosApp()) {
    window.webkit.messageHandlers[method].postMessage(code);
    return false;
  } else if (isInUqHkAndroidApp()) {
    window.android[method](code);
    return false;
  } else {
    return true;
  }
};

// GUTW App login
export const isInGuTwIosApp = () => getUA.indexOf(GU_TW_Ios_UserAgent) > -1;
export const isInGuTwAndroidApp = () =>
  getUA.indexOf(GU_TW_Android_UserAgent) > -1;
export const iosAndroidGuTw = (method: string) => {
  if (isInGuTwIosApp()) {
    window.webkit.messageHandlers[method].postMessage("");
    return false;
  } else if (isInGuTwAndroidApp()) {
    window.android[method]("", "", "");
    return false;
  } else {
    return true;
  }
};

// UQTW App login
export const isInUqTwIosApp = () => getUA.indexOf(UQ_TW_Ios_UserAgent) > -1;
export const isInUqTwAndroidApp = () =>
  getUA.indexOf(UQ_TW_Android_UserAgent) > -1;
export const iosAndroidUqTw = (method: string) => {
  if (isInUqTwIosApp()) {
    window.webkit.messageHandlers[method].postMessage("");
    return false;
  } else if (isInUqTwAndroidApp()) {
    window.android[method]("", "", "");
    return false;
  } else {
    return true;
  }
};

export const replacePlaceholder = (
  value: Maybe<string>,
  options: { [key: string]: number | string }
): string => {
  if (!value) {
    return "";
  }
  let res = value;

  Object.keys(options).forEach((k) => {
    const v = options[k];
    if (v || v === 0) {
      // We exclude 0, because there are times we actually want to put in the value 0 in the placeholder
      // replace all occurences
      const wordingVarRegex = new RegExp(`${k}`, "g");
      res = res.replace(wordingVarRegex, String(v));
    }
  });

  return res;
};

// Is PC Mode
export const isPC = (brand: string, region: string): boolean => {
  switch (`${brand}${region}`) {
    case `${BRAND_LIST.UQ}${COUNTRY.TW}`:
    case `${BRAND_LIST.GU}${COUNTRY.TW}`:
    case `${BRAND_LIST.GU}${COUNTRY.HK}`:
    case `${BRAND_LIST.UQ}${COUNTRY.HK}`: {
      const matchAgent = [
        "android",
        "iphone",
        "symbianos",
        "windows phone",
        "ipod",
        "mobile"
      ];
      const _matchAgent = matchAgent.filter(
        (agent) => getUA.toLowerCase().indexOf(agent) === -1
      );
      return _matchAgent.length === matchAgent.length;
    }
    default:
      return true;
  }
};

export const handleSymbolReplace = (str: string): string =>
  str.replace("_", "-") || "zh-HK";

// StyleHint styling detail -> item size, returns placeholder string for "No Control"
export const getSizeForItemInfo = (size?: string) => {
  const placeholder = "-";
  if (!size || size === "No Control") {
    return placeholder;
  }
  return size;
};

// Should display size label on StyleHint detail page item area
export const isDisplaySizeLabel = (size?: string): boolean =>
  getSizeForItemInfo(size) !== "-";

// UserType is Staff
export const isStaff = (type: UserTypeTypes): boolean =>
  type.toUpperCase() === UserTypeEnum.STAFF;
